import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Card from '../../common/Card'
import { detailsActions } from '../../../store/details'
import { tabs_button } from '../../../assets/data'
import PatientTabButton from './PatientTabButton'
import PatientsTabsOnPageChange from './PatientsTabsOnPageChange'
import classes from './PatientTabs.module.css'
import { historyActions } from '../../../store/history'
import { isTabActiveAndAccesible } from '../../../lib/script'
import { computeVisiblePatientTabItems, getCaseTypeTabConfig } from '../../../lib/patientTabVisibility'
import Button from '../../common/Button'
import { viewTabPermission } from '../../../lib/permissions'

import AttorneyTab from '../details/attorney/AttorneyTab'
import LiabilityTab from '../details/liability/LiabilityTab'
import MedpayTab from '../details/medpay/MedpayTab'
import UmTab from '../details/um/UmTab'
import MajorMedTab from '../details/majormed/MajorMedTab'
import CrimeVictimTab from '../details/crimevictim/CrimeVictimTab'
import WorkersCompTab from '../details/workersComp/WorkersCompTab'
import ServicesTab from '../details/services/ServicesTab'
import CaseSummaryTab from '../details/casesummary/CaseSummaryTab'
import Transportation from '../details/transportation/Transportation'
import DocumentsTab from '../details/documents/DocumentsTab'
import NotesTab from '../details/notes/NotesTab'
import SurgicalTab from '../details/surgical/SurgicalTab'
import LienTab from '../details/lien/LienTab'
import AppointmentsTab from '../details/appointments/AppointmentsTab'
import NegotiationTab from '../details/negotiation/NegotiationTab'
import SOSTab from '../details/sos/SOSTab'
import SelfPayTab from '../details/selfpay/SelfPayTab'
import ProcoDocsTab from '../details/procodocs/ProcoDocsTab'
import FlagsTab from '../details/flags/FlagsTab'
import HistoryTab from '../details/history/HistoryTab'
import AlertsTab from '../details/alerts/AlertsTab'
import { TAB_IDS } from '../../../assets/data'

const PatientTabs = () => {
    
    const dispatch = useDispatch()
    const activeCase = useSelector(state => state.case.activeCase) 
    const user = useSelector(state => state.user.user)
    const editPermissions = useSelector(state => state.permissions.editPermissions)
    const hasViewFlags = (permObj) => (
        !!permObj &&
        Object.keys(permObj).some((key) => key.endsWith('_view'))
    )
    const userPermissions = user?.permissions
    const effectivePermissions = hasViewFlags(userPermissions)
        ? userPermissions
        : (hasViewFlags(editPermissions) ? editPermissions : null)

    const permissionTabs = useMemo(() => {
        const resolved = viewTabPermission(tabs_button, effectivePermissions)
        return Array.isArray(resolved) ? resolved : []
    }, [effectivePermissions])
    const permissionsAreLoaded = !!effectivePermissions

    const [viewAll, setViewAll] = useState(false)

    const switchTabHandler = (index) => {
        dispatch(detailsActions.setActiveTab(index))
    }
    const activeTab = useSelector(state => state.details.detailsTabs)  
    const hiddenTabIds = useMemo(() => new Set([TAB_IDS.FLAGS, TAB_IDS.HISTORY, TAB_IDS.ALERTS, TAB_IDS.APPOINTMENTS]), [])
    
    // Derive visible tabs directly to avoid an extra render cycle from setState.
    const visibleTabs = useMemo(() => {
        if (!permissionsAreLoaded) {
            return []
        }

        // "View all" should show all available tabs (by permission),
        // ignoring case-type based visibility filtering.
        if (viewAll) {
            return permissionTabs
        }

        let baseTabs = permissionTabs
        if (activeCase?.case?.case_type_id) {
            const foundItem = getCaseTypeTabConfig(activeCase)
            if (foundItem) {
                baseTabs = computeVisiblePatientTabItems({
                    activeCase,
                    permission_tabs: permissionTabs,
                    foundItem,
                })
            }
        }

        return baseTabs.filter((item) => !hiddenTabIds.has(item.id))
    }, [activeCase, viewAll, permissionTabs, permissionsAreLoaded, hiddenTabIds])

    useEffect(() => {
        return () => {
            dispatch(historyActions.clearFilters())
            dispatch(historyActions.setCurrentPageData(null))
            dispatch(historyActions.setCurrentPage(1))
        }
    }, [dispatch])

    function viewAllTabs() {
        setViewAll(prev => !prev)
    }

    return <Card border={0}>
        <PatientsTabsOnPageChange activeTab={activeTab} tabs={visibleTabs}/>
        {/* <div className={classes.tabs_container_wrapper}> */}
            <div className={classes.tabs_container}>   
                <div className={classes.tabs_container_wrapper}>
                    {!permissionsAreLoaded && <div className={classes.tab}>Loading tabs...</div>}
                    {visibleTabs?.map((item, index) => (
                        <PatientTabButton key={item.id} item={item} index={index} clickAction={switchTabHandler} tabs={visibleTabs}/>
                    ))}
                </div>
                <Button click={viewAllTabs} title={viewAll ? 'Hide extra tabs' : 'View all'}>
                    {!viewAll &&<i className='ti ti-eye'></i>}
                    {viewAll && <i className='ti ti-eye-off'></i>}
                </Button>
            </div>
        {/* </div> */}
        <div className={classes.tab_pannel}>


            {isTabActiveAndAccesible(activeTab , 0, permissionTabs) && <AttorneyTab />}


            {isTabActiveAndAccesible(activeTab , 1, permissionTabs) && <LiabilityTab />}


            {isTabActiveAndAccesible(activeTab , 2, permissionTabs) && <MedpayTab />}


            {isTabActiveAndAccesible(activeTab , 3, permissionTabs) && <UmTab />}


            {isTabActiveAndAccesible(activeTab , 5, permissionTabs) && <MajorMedTab />}


            {isTabActiveAndAccesible(activeTab , 4, permissionTabs) && <CrimeVictimTab />}


            {isTabActiveAndAccesible(activeTab , 6, permissionTabs) && <WorkersCompTab />}


            {isTabActiveAndAccesible(activeTab , 9, permissionTabs) && <ServicesTab />}


            {isTabActiveAndAccesible(activeTab , 7, permissionTabs) && <CaseSummaryTab />}


            {isTabActiveAndAccesible(activeTab , 8, permissionTabs) && <Transportation />}


            {isTabActiveAndAccesible(activeTab , 11, permissionTabs) && <DocumentsTab />}


            {isTabActiveAndAccesible(activeTab , 12, permissionTabs) && <NotesTab />}


            {isTabActiveAndAccesible(activeTab , 13, permissionTabs) && <SurgicalTab />}


            {isTabActiveAndAccesible(activeTab , 14, permissionTabs) && <LienTab />}


            {isTabActiveAndAccesible(activeTab , 15, permissionTabs) && <AppointmentsTab />}


            {isTabActiveAndAccesible(activeTab , 16, permissionTabs) && <NegotiationTab />}


            {isTabActiveAndAccesible(activeTab , 17, permissionTabs) && <SOSTab />}


            {isTabActiveAndAccesible(activeTab , 18, permissionTabs) && <SelfPayTab />}


            {isTabActiveAndAccesible(activeTab , 19, permissionTabs) && <ProcoDocsTab />}





            {isTabActiveAndAccesible(activeTab , 26, permissionTabs) && <FlagsTab />}


            {isTabActiveAndAccesible(activeTab , 27, permissionTabs) && <HistoryTab />}


            {isTabActiveAndAccesible(activeTab , 36, permissionTabs)&& <AlertsTab />}

        </div>
        {/* <div className={classes.tab_pannel}>
            {activeTab && <TabRenderer tab={activeTab} permission_tabs={permission_tabs} />}
        </div> */}
    </Card>
}

export default PatientTabs